/**
 * Which signature a composer starts with, and how to find one by id.
 *
 * Separate from `editorSafeHtml.js` on purpose: that module answers "what HTML
 * does this signature become", this one answers "which signature". Both the
 * shared `TemplateSignatureSelectors` component and every caller that SEEDS a
 * signature when a composer opens read the precedence from here, so the
 * dropdown's selection and the body's content can never disagree about which
 * signature is in play.
 */

/**
 * Personal signatures followed by org-managed ones, as one list.
 *
 * Order matters only for `find`: a personal signature wins an id collision,
 * which is the conservative choice since it is the one the user owns.
 */
export function allSignatures(signatures, adminSignatures) {
    return [...(signatures || []), ...(adminSignatures || [])];
}

export function findSignature(signatures, adminSignatures, signatureId) {
    if (!signatureId || signatureId === 'none') return null;
    return allSignatures(signatures, adminSignatures)
        .find(s => s.signature_id === signatureId) || null;
}

/**
 * The signature a composer should open with.
 *
 * Precedence, highest first:
 *
 *  1. the org-wide override (`active_override_id` on `GET /api/signatures`), if
 *     it names a signature this user can actually see;
 *  2. the user's default signature;
 *  3. nothing.
 *
 * The override is PRESELECTED, not locked (decision 3 on issue #597): the
 * dropdown stays enabled and **None** stays available. This is deliberately
 * softer than the issue's "fixed by the organisation rather than a free
 * choice" — it is preselected and labelled as the organisation's, and the user
 * may still choose otherwise.
 *
 * @returns {object|null} the signature record, or null when none applies
 */
export function pickSeedSignature({
    activeOverrideId,
    signatures,
    adminSignatures,
    defaultSignature,
} = {}) {
    const override = findSignature(signatures, adminSignatures, activeOverrideId);
    if (override) return override;
    return defaultSignature || null;
}

/** True when this signature is the one the organisation has forced. */
export function isOrgOverride(signature, activeOverrideId) {
    return Boolean(
        signature && activeOverrideId && signature.signature_id === activeOverrideId,
    );
}
